/**
 * MobileFilterSheet — bottom-sheet «Фильтры» для мобилки (design 02_clients,
 * экраны B и C): чипы Статус/Тип + строки-переходы Тариф/Город во вложенный
 * экран мультивыбора с поиском. Применение — сразу (live), «Показать N»
 * просто закрывает лист.
 */

import {type ReactNode, useState} from 'react';
import {Building2, Check, ChevronLeft, ChevronRight, User, X} from 'lucide-react';

import {type SubscriptionStatus, TARIFF_OPTIONS} from '@services/clients';
import {useCityOptions, useStatusOptions} from '@hooks/useFilterRefs';
import {SearchField} from '@components/SearchField';
import {SheetGrabber} from '@components/SheetGrabber';
import {Sheet, SheetContent} from '@components/ui/Sheet';
import {cn} from '@lib/utils';
import {formatNumber} from '@utils/format';

import {type ClientsFilters, selectedFilterCount} from '../Clients.utils';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: ClientsFilters;
  patch: (p: Partial<ClientsFilters>) => void;
  totalCount: number;
};

export const MobileFilterSheet = ({open, onOpenChange, filters, patch, totalCount}: Props) => {
  const [screen, setScreen] = useState<'root' | 'city' | 'tariff'>('root');
  const statusOptions = useStatusOptions();
  const cityOptions = useCityOptions();
  const selectedCount = selectedFilterCount(filters);
  const reset = () => patch({status: null, type: null, cities: [], tariffs: [], page: 1});

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showClose={false}
        className="max-h-[88%] gap-0 overflow-y-auto rounded-t-3xl p-5"
      >
        {screen === 'root' && (
          <>
            <SheetGrabber className="mb-3" />
            <div className="mb-4 flex items-center justify-between gap-3">
              <span className="text-lg font-bold -tracking-[.01em]">Фильтры</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={reset}
                  disabled={selectedCount === 0}
                  className="text-[13.5px] font-medium text-primary transition-opacity disabled:pointer-events-none disabled:opacity-40"
                >
                  Сбросить
                </button>
                <button
                  onClick={() => onOpenChange(false)}
                  aria-label="Закрыть"
                  className="grid size-8 place-items-center rounded-full bg-secondary text-muted-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            <FieldLabel>Статус</FieldLabel>
            <div className="mb-5 flex flex-wrap gap-2">
              <Chip active={filters.status === null} onClick={() => patch({status: null, page: 1})}>
                Все
              </Chip>
              {statusOptions.map((o) => (
                <Chip
                  key={o.value}
                  active={filters.status === o.value}
                  onClick={() => patch({status: o.value as SubscriptionStatus, page: 1})}
                >
                  <span className="size-[7px] rounded-full" style={{background: o.dot}} />
                  {o.label}
                </Chip>
              ))}
            </div>

            <FieldLabel>Тип</FieldLabel>
            <div className="mb-5 flex flex-wrap gap-2">
              <Chip active={filters.type === null} onClick={() => patch({type: null, page: 1})}>
                Все
              </Chip>
              <Chip
                active={filters.type === 'clinic'}
                onClick={() => patch({type: 'clinic', page: 1})}
              >
                <Building2 className="size-3.5" strokeWidth={1.8} />
                Клиника
              </Chip>
              <Chip active={filters.type === 'solo'} onClick={() => patch({type: 'solo', page: 1})}>
                <User className="size-3.5" strokeWidth={1.8} />
                Соло
              </Chip>
            </div>

            <RowLink
              label="Тариф"
              value={filters.tariffs.length ? `${filters.tariffs.length}` : 'Все'}
              onClick={() => setScreen('tariff')}
            />
            <RowLink
              label="Город"
              value={filters.cities.length ? `${filters.cities.length}` : 'Все'}
              onClick={() => setScreen('city')}
            />

            <button
              className="mt-5 h-[50px] w-full rounded-[13px] bg-primary text-[15.5px] font-semibold text-primary-foreground shadow-[0_4px_10px_rgba(44,92,219,.28)]"
              onClick={() => onOpenChange(false)}
            >
              Показать {formatNumber(totalCount)}
            </button>
          </>
        )}

        {screen === 'city' && (
          <PickerScreen
            title="Город"
            searchable
            options={cityOptions}
            selected={filters.cities}
            onToggle={(next) => patch({cities: next, page: 1})}
            onBack={() => setScreen('root')}
          />
        )}
        {screen === 'tariff' && (
          <PickerScreen
            title="Тариф"
            options={TARIFF_OPTIONS}
            selected={filters.tariffs}
            onToggle={(next) => patch({tariffs: next, page: 1})}
            onBack={() => setScreen('root')}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

const FieldLabel = ({children}: {children: ReactNode}) => (
  <div className="mb-2.5 text-[12.5px] font-semibold text-skr-ink2">{children}</div>
);

type ChipProps = {active: boolean; onClick: () => void; children: ReactNode};
const Chip = ({active, onClick, children}: ChipProps) => (
  <button
    onClick={onClick}
    className={cn(
      'inline-flex h-[34px] items-center gap-1.5 rounded-[10px] border px-[13px] text-[13px]',
      active
        ? 'border-skr-info-line bg-accent font-semibold text-primary'
        : 'border-skr-line bg-card text-skr-ink2',
    )}
  >
    {children}
  </button>
);

type RowLinkProps = {label: string; value: string; onClick: () => void};
const RowLink = ({label, value, onClick}: RowLinkProps) => (
  <button
    onClick={onClick}
    className="flex w-full items-center justify-between border-t border-skr-hairline py-3.5 text-left"
  >
    <span className="text-[14.5px] text-foreground">{label}</span>
    <span className="flex items-center gap-1.5 text-[14px] text-muted-foreground">
      {value}
      <ChevronRight className="size-[17px] text-skr-chevron" strokeWidth={2} />
    </span>
  </button>
);

type PickerProps = {
  title: string;
  options: readonly string[];
  selected: string[];
  onToggle: (next: string[]) => void;
  onBack: () => void;
  searchable?: boolean;
};
const PickerScreen = ({title, options, selected, onToggle, onBack, searchable}: PickerProps) => {
  const [search, setSearch] = useState('');
  const shown = options.filter((o) => o.toLowerCase().includes(search.trim().toLowerCase()));
  const toggle = (opt: string) =>
    onToggle(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);

  return (
    <>
      <div className="mb-2 flex items-center gap-2.5">
        <button
          onClick={onBack}
          className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-secondary"
        >
          <ChevronLeft className="size-5 text-skr-ink" strokeWidth={2} />
        </button>
        <div className="flex-1 text-[17px] font-bold -tracking-[.01em]">{title}</div>
        <button
          onClick={() => onToggle([])}
          disabled={selected.length === 0}
          className="text-[13.5px] font-medium text-primary transition-opacity disabled:pointer-events-none disabled:opacity-40"
        >
          Сбросить
        </button>
      </div>

      {searchable && (
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={`Поиск: ${title.toLowerCase()}`}
          className="mb-2 h-11 rounded-xl border border-input bg-muted/40 px-[13px]"
        />
      )}

      <div className="-mx-1">
        {shown.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className="flex w-full items-center justify-between border-b border-skr-hairline px-3 py-3.5 text-left"
            >
              <span className="text-[15px]">{opt}</span>
              <span
                className={cn(
                  'grid size-[22px] place-items-center rounded-[7px] border',
                  on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                )}
              >
                {on && <Check className="size-3.5" strokeWidth={3} />}
              </span>
            </button>
          );
        })}
      </div>

      <button
        className="mt-4 flex h-[50px] w-full items-center justify-center gap-2 rounded-[13px] bg-primary text-[15.5px] font-semibold text-primary-foreground"
        onClick={onBack}
      >
        Готово
        {selected.length > 0 && (
          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-white/20 px-1.5 text-[12px] font-semibold">
            {selected.length}
          </span>
        )}
      </button>
    </>
  );
};
